import React, { useState } from "react";
import { TableCell, TableRow, Typography, Stack, Chip, Box } from "@mui/material";
import TableCommon from "../../Common/Table";
import { handleDateTimeDefault } from "../../../helpers/utils";
import { useUpdateAlertMutation, useDeleteAlertMutation } from "../../../store/alerts/alertsApis";
import ActionMenu, { ActionTypes } from "../../Common/ActionMenu";
import ConfirmationDialog from "../../Common/ConfirmationDialog";
import { setSnackBar } from "../../../store/common/commonSlice";
import { useDispatch } from "react-redux";

const AlertsTable = ({ loading, fetching = false, count, data, edit, pagination, handlePagination }) => {
    const dispatch = useDispatch();
    const [updateAlert] = useUpdateAlertMutation();
    const [deleteAlert] = useDeleteAlertMutation();
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [alertToToggle, setAlertToToggle] = useState(null);

    const handleStatusToggle = (row) => {
        setAlertToToggle(row);
        setConfirmationOpen(true);
    };

    const handleConfirmToggle = async () => {
        try {
            await updateAlert({
                id: alertToToggle?.id,
                is_active: !alertToToggle?.is_active,
            }).unwrap();
            
            dispatch(
                setSnackBar({
                    open: true,
                    message: `Alert ${alertToToggle?.is_active ? "deactivated" : "activated"} successfully`,
                    severity: "success",
                }),
            );
        } catch (error) {
            dispatch(
                setSnackBar({
                    open: true,
                    message: error?.data?.message || "Failed to update alert status",
                    severity: "error",
                }),
            );
        }
        setConfirmationOpen(false);
    };

    const handleCloseConfirmation = () => {
        setConfirmationOpen(false);
    };

    const getStatusColor = (alert) => {
        if (!alert.is_active) return "default";
        const expiryDate = new Date(alert.expiry_date);
        const now = new Date();
        if (expiryDate < now) return "error";
        return "success";
    };

    const getStatusLabel = (alert) => {
        if (!alert.is_active) return "Inactive";
        const expiryDate = new Date(alert.expiry_date);
        const now = new Date();
        if (expiryDate < now) return "Expired";
        return "Active";
    };

    const getTargetTypeLabel = (targetType) => {
        switch (targetType) {
            case "all":
                return "All Users";
            case "roles":
                return "Specific Roles";
            case "users":
                return "Specific Users";
            default:
                return targetType;
        }
    };

    const columns = [
        {
            title: "ID",
            field: "id",
            sort: true,
            width: "8%",
            align: "center",
        },
        {
            title: "Title",
            field: "title",
            sort: true,
            width: "25%",
            align: "left",
        },
        {
            title: "Target",
            field: "target_type",
            sort: true,
            width: "15%",
            align: "center",
        },
        {
            title: "Status",
            field: "is_active",
            sort: false,
            width: "12%",
            align: "center",
        },
        {
            title: "Reads",
            field: "read_count",
            sort: true,
            width: "10%",
            align: "center",
        },
        {
            title: "Expiry Date",
            field: "expiry_date",
            sort: true,
            width: "15%",
            align: "center",
        },
        {
            title: "Created Date",
            field: "created_at",
            sort: true,
            width: "15%",
            align: "center",
        },
        {
            title: "",
            name: "",
            sort: false,
            width: "10%",
            align: "center",
        },
    ];

    const renderTableData =
        !loading && data && data.length > 0 ? (
            data.map((row, index) => {
                const isExpired = new Date(row.expiry_date) < new Date();
                
                return (
                    <TableRow
                        key={index}
                        sx={{
                            "td, th": {
                                border: 0,
                                padding: "16px 12px",
                                verticalAlign: "middle",
                            },
                            ...(row?.is_active === false && {
                                opacity: 0.6,
                                backgroundColor: "#fafafa",
                            }),
                            ...(isExpired && {
                                backgroundColor: "#fff3e0",
                            }),
                            "&:hover": {
                                backgroundColor: "#f8f9fa",
                            },
                            transition: "all 0.2s ease-in-out",
                        }}
                    >
                        <TableCell align="center">
                            <Chip
                                label={row.id}
                                size="small"
                                sx={{
                                    backgroundColor: "#e3f2fd",
                                    color: "#1976d2",
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                    height: 24,
                                }}
                            />
                        </TableCell>
                        <TableCell align="left">
                            <Stack spacing={0.5}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        color: "#2c3e50",
                                        fontSize: "0.875rem",
                                    }}
                                >
                                    {row?.title}
                                </Typography>
                                {row.images && row.images.length > 0 && (
                                    <Chip
                                        label={`${row.images.length} ${row.images.length === 1 ? "image" : "images"}`}
                                        size="small"
                                        sx={{
                                            backgroundColor: "#e8f5e9",
                                            color: "#2e7d32",
                                            fontSize: "0.7rem",
                                            height: 20,
                                            width: "fit-content",
                                        }}
                                    />
                                )}
                            </Stack>
                        </TableCell>
                        <TableCell align="center">
                            <Chip
                                label={getTargetTypeLabel(row.target_type)}
                                size="small"
                                variant="outlined"
                                sx={{
                                    fontSize: "0.75rem",
                                    height: 24,
                                }}
                            />
                        </TableCell>
                        <TableCell align="center">
                            <Chip
                                label={getStatusLabel(row)}
                                size="small"
                                color={getStatusColor(row)}
                                sx={{
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    height: 24,
                                }}
                            />
                        </TableCell>
                        <TableCell align="center">
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    backgroundColor: "#e3f2fd",
                                    color: "#1976d2",
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                }}
                            >
                                {row.read_count || 0}
                            </Box>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body2"
                                sx={{
                                    color: isExpired ? "#d32f2f" : "#666",
                                    fontSize: "0.75rem",
                                    fontWeight: isExpired ? 600 : 400,
                                }}
                            >
                                {handleDateTimeDefault(row?.expiry_date)}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography variant="body2" sx={{ color: "#666", fontSize: "0.75rem" }}>
                                {handleDateTimeDefault(row?.created_at)}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <ActionMenu
                                actions={[
                                    {
                                        ...ActionTypes.EDIT,
                                        onClick: () => edit(row, "Edit"),
                                    },
                                    {
                                        ...ActionTypes.VIEW,
                                        onClick: () => edit(row, "View"),
                                    },
                                ]}
                                row={row}
                                onStatusToggle={handleStatusToggle}
                            />
                        </TableCell>
                    </TableRow>
                );
            })
        ) : (
            <TableRow sx={{ "td, th": { border: 0, padding: "10px" } }}>
                <TableCell colSpan={8} align="center">
                    <Typography variant="body2" sx={{ color: "#666", py: 4 }}>
                        {loading ? "Loading alerts..." : "No alerts found"}
                    </Typography>
                </TableCell>
            </TableRow>
        );

    return (
        <>
            <TableCommon
                columns={columns}
                tableData={renderTableData}
                count={count}
                loading={loading || fetching}
                pagination={pagination}
                handlePagination={handlePagination}
                emptyMessage="No alerts found"
            />

            <ConfirmationDialog
                open={confirmationOpen}
                onClose={handleCloseConfirmation}
                onConfirm={handleConfirmToggle}
                title={
                    alertToToggle?.is_active === false
                        ? "Are you sure you want to activate this alert?"
                        : "Are you sure you want to deactivate this alert?"
                }
                confirmButtonTitle={alertToToggle?.is_active === false ? "Yes, Activate" : "Yes, Deactivate"}
            />
        </>
    );
};

export default AlertsTable;

